import type {
  IMergeTag,
  IMergeTagArray,
} from "@/schemas/mergeTags/mergeTagsSchema";

/**
 * True when the value stands on its own — as an href, an attribute value or a
 * listbox entry. An HTML snippet or a value carrying entities does not: those
 * only make sense pasted into content.
 *
 * (`includes("</")` is implied by `includes("<")`, hence the two checks.)
 */
export function isPlainMergeTag(value: string): boolean {
  return !value.includes("<") && !value.includes("&");
}

type MergeTagTreeMapper<T> = {
  /** Return null to drop the leaf — and with it any group it leaves empty. */
  mapLeaf: (mergeTag: IMergeTag) => T | null;
  mapGroup: (name: string, children: T[]) => T;
};

/**
 * Walks the grouped merge-tag tree and rebuilds it in whatever shape the
 * consumer needs: TinyMCE's `link_list`, the visual-merge-tags listbox, a flat
 * autocomplete list. Each of those nests and names its fields differently, so
 * the shape is the caller's business — the walk, and dropping groups that
 * `mapLeaf` emptied, is not. The input is never mutated.
 */
export function mapMergeTagTree<T>(
  mergeTags: IMergeTagArray,
  mapper: MergeTagTreeMapper<T>
): T[] {
  const result: T[] = [];

  mergeTags.forEach((item) => {
    if ("items" in item) {
      const children = mapMergeTagTree(item.items, mapper);
      if (children.length > 0) {
        result.push(mapper.mapGroup(item.name, children));
      }
      return;
    }

    const mapped = mapper.mapLeaf(item);
    if (mapped !== null) {
      result.push(mapped);
    }
  });

  return result;
}

/** Every leaf of the tree in tree order, groups flattened away. */
export function flattenMergeTags(mergeTags: IMergeTagArray): IMergeTag[] {
  return mapMergeTagTree<IMergeTag[]>(mergeTags, {
    mapLeaf: (mergeTag) => [mergeTag],
    mapGroup: (_name, children) => children.flat(),
  }).flat();
}
